/**
 * ContractsPage — Contracts management (Phase 2: list + create + edit)
 * ────────────────────────────────────────────────────────────────────
 * Lists Contracts (Engine 1 container) and exposes inline create (AddContractRow)
 * and inline edit (ContractRow read↔edit toggle), modeled on SiteManagementPage's
 * SiteRow/AddSiteRow. contract_id + site_id are the immutable locator/PK pair —
 * shown read-only in edit mode; only customer_po + dwell_auto_disposition are
 * editable. Role-gated to the warehouse WRITE roles (ADMIN / FIELD_REP) — the same
 * gate manage_contract enforces — via the wh-lock pattern.
 *
 * Reuses the warehouse design system (wh-* classes + var(--token)); no new CSS.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import { useUser } from '../../../shared/context/UserContext';
import { getSites } from '../../inventory/api/inventoryApi';
import { getContracts, createContract, updateContract, deleteContract } from '../api/contractsApi';
import '../warehouse.css';

const WAREHOUSE_WRITE_ROLES = new Set(['ADMIN', 'FIELD_REP']);
const COL_COUNT = 5;

/* Token-styled checkbox label (mirrors WarehouseItemsPage.jsx ~:400; the app has
   no switch component — a plain checkbox is the boolean idiom). */
const CHECKBOX_LABEL_STYLE = {
  display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)',
  fontSize: 'var(--text-sm)', color: 'var(--color-text)',
};

function siteLabel(s) {
  if (s.site_city) return `${s.site_name} (${s.site_city})`;
  return s.site_name || 'Unnamed Site';
}

/* Token-styled On/Off badge for dwell_auto_disposition (mirrors the warehouse
   page's BADGE_BASE idiom; var(--token) only — no new CSS). */
const BADGE_BASE = {
  display: 'inline-flex', alignItems: 'center',
  padding: 'var(--space-1) var(--space-4)', borderRadius: 'var(--radius-pill)',
  fontSize: 'var(--text-2xs)', fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
};
function AutoDispoBadge({ on }) {
  const variant = on
    ? { background: 'var(--color-warning-light)', color: 'var(--color-warning-dark)' }
    : { background: 'var(--color-bg-subtle)', color: 'var(--color-text-secondary)' };
  return <span style={{ ...BADGE_BASE, ...variant }}>{on ? 'On' : 'Off'}</span>;
}

/* ── ContractRow — read↔edit toggle (mirrors SiteManagementPage SiteRow) ───── */
function ContractRow({ contract, label, callerEmail, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [customerPo, setCustomerPo] = useState(contract.customer_po || '');
  const [autoDispo, setAutoDispo] = useState(contract.dwell_auto_disposition === true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  function startEdit() {
    setCustomerPo(contract.customer_po || '');
    setAutoDispo(contract.dwell_auto_disposition === true);
    setError(null);
    setEditing(true);
  }
  function cancelEdit() { setEditing(false); setError(null); }

  async function handleDelete() {
    if (!window.confirm(`Delete contract ${contract.contract_id}? This cannot be undone.`)) return;
    setDeleting(true);
    setError(null);
    try {
      // site_id is the stored locator (immutable) — sent, not user-editable.
      await deleteContract(callerEmail, {
        contract_id: contract.contract_id,
        site_id: contract.site_id,
      });
      await onSaved();
    } catch (err) {
      // 409 "has N warehouse item(s)" (or any failure) renders inline, never silent.
      setError(err.message);
      setDeleting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      // site_id is the stored locator (immutable) — sent, not user-editable.
      await updateContract(callerEmail, {
        contract_id: contract.contract_id,
        site_id: contract.site_id,
        customer_po: customerPo.trim() || null,
        dwell_auto_disposition: autoDispo,
      });
      setEditing(false);
      await onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <>
        <tr className="wh-row">
          <td className="wh-td">{contract.contract_id || '—'}</td>
          <td className="wh-td">{label}</td>
          <td className="wh-td">{contract.customer_po || '—'}</td>
          <td className="wh-td"><AutoDispoBadge on={contract.dwell_auto_disposition === true} /></td>
          <td className="wh-td">
            <button type="button" className="wh-action-btn" onClick={startEdit} disabled={deleting}>Edit</button>
            <button
              type="button" className="wh-btn-danger" onClick={handleDelete} disabled={deleting}
              style={{ marginLeft: 'var(--space-3)' }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </td>
        </tr>
        {error && (
          <tr><td className="wh-td" colSpan={COL_COUNT}><div className="wh-form-error">{error}</div></td></tr>
        )}
      </>
    );
  }

  return (
    <>
      <tr className="wh-row">
        {/* contract_id + site_id are the immutable PK pair — read-only static text */}
        <td className="wh-td">{contract.contract_id}</td>
        <td className="wh-td">{label}</td>
        <td className="wh-td">
          <input
            type="text" className="wh-input wh-input-narrow" value={customerPo}
            onChange={(e) => setCustomerPo(e.target.value)} placeholder="Customer PO"
          />
        </td>
        <td className="wh-td">
          <label style={CHECKBOX_LABEL_STYLE}>
            <input type="checkbox" checked={autoDispo} onChange={(e) => setAutoDispo(e.target.checked)} />
            Auto-disposition
          </label>
        </td>
        <td className="wh-td">
          <button type="button" className="wh-action-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button" className="wh-action-btn" onClick={cancelEdit} disabled={saving}
            style={{ marginLeft: 'var(--space-3)' }}
          >
            Cancel
          </button>
        </td>
      </tr>
      {error && (
        <tr><td className="wh-td" colSpan={COL_COUNT}><div className="wh-form-error">{error}</div></td></tr>
      )}
    </>
  );
}

/* ── AddContractRow — inline create (mirrors SiteManagementPage AddSiteRow) ──── */
function AddContractRow({ sites, callerEmail, onSaved, onCancel }) {
  const [contractId, setContractId] = useState('');
  const [siteId, setSiteId] = useState(sites.length === 1 ? sites[0].site_id : '');
  const [customerPo, setCustomerPo] = useState('');
  const [autoDispo, setAutoDispo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [contractIdError, setContractIdError] = useState(null);

  const canSubmit = !!contractId.trim() && !!siteId && !saving;

  async function handleSave() {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    setContractIdError(null);
    try {
      await createContract(callerEmail, {
        site_id: siteId,
        contract_id: contractId.trim(),
        customer_po: customerPo.trim() || null,
        dwell_auto_disposition: autoDispo,
      });
      await onSaved();
      onCancel(); // close + reset (component unmounts)
    } catch (err) {
      // 409 "already exists at this site" → readable inline error on contract_id.
      if (/already exists/i.test(err.message || '')) setContractIdError(err.message);
      else setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <tr className="wh-row">
        <td className="wh-td">
          <input
            type="text" className="wh-input wh-input-narrow" value={contractId}
            onChange={(e) => { setContractId(e.target.value); setContractIdError(null); }}
            placeholder="Contract ID" autoFocus
          />
          {contractIdError && <div className="wh-error-text">{contractIdError}</div>}
        </td>
        <td className="wh-td">
          <select className="wh-input" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">Select site…</option>
            {sites.map((s) => <option key={s.site_id} value={s.site_id}>{siteLabel(s)}</option>)}
          </select>
        </td>
        <td className="wh-td">
          <input
            type="text" className="wh-input wh-input-narrow" value={customerPo}
            onChange={(e) => setCustomerPo(e.target.value)} placeholder="Customer PO (optional)"
          />
        </td>
        <td className="wh-td">
          <label style={CHECKBOX_LABEL_STYLE}>
            <input type="checkbox" checked={autoDispo} onChange={(e) => setAutoDispo(e.target.checked)} />
            Auto-disposition
          </label>
          <div className="wh-hint">Configuration only today — auto-disposition does not fire yet (Engine 1).</div>
        </td>
        <td className="wh-td">
          <button type="button" className="wh-action-btn" onClick={handleSave} disabled={!canSubmit}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button" className="wh-action-btn" onClick={onCancel} disabled={saving}
            style={{ marginLeft: 'var(--space-3)' }}
          >
            Cancel
          </button>
        </td>
      </tr>
      {error && (
        <tr><td className="wh-td" colSpan={COL_COUNT}><div className="wh-form-error">{error}</div></td></tr>
      )}
    </>
  );
}

export default function ContractsPage() {
  const { callerEmail, userProfile } = useUser();
  const role = userProfile?.role;

  const [contracts, setContracts] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);

  /* Refetch just the contract list (sites are static for the session). Reused as
     the onSaved handler after a create/edit. */
  const loadContracts = useCallback(() => {
    if (!callerEmail) return Promise.resolve();
    return getContracts(callerEmail)
      .then((d) => setContracts(Array.isArray(d?.contracts) ? d.contracts : []))
      .catch((err) => setError(err.message));
  }, [callerEmail]);

  useEffect(() => {
    if (!callerEmail) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getContracts(callerEmail),
      getSites(callerEmail, 'WAREHOUSE').catch(() => []),
    ])
      .then(([contractData, siteData]) => {
        if (cancelled) return;
        setContracts(Array.isArray(contractData?.contracts) ? contractData.contracts : []);
        setSites(Array.isArray(siteData) ? siteData : siteData?.sites || []);
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [callerEmail]);

  const siteLabelById = useMemo(() => {
    const m = {};
    for (const s of sites) m[s.site_id] = siteLabel(s);
    return m;
  }, [sites]);

  /* Role gate — ADMIN / FIELD_REP only (manage_contract write gate; wh-lock pattern) */
  if (role && !WAREHOUSE_WRITE_ROLES.has(role)) {
    return (
      <div className="wh-lock">
        <Lock size={48} />
        <h1>You do not have access</h1>
      </div>
    );
  }

  return (
    <div className="wh-page">
      <div className="wh-header">
        <h1 className="wh-header-title">Contracts</h1>
        {!loading && !error && (
          <span className="wh-header-meta">{contracts.length} contract{contracts.length === 1 ? '' : 's'}</span>
        )}
        <span className="wh-header-spacer" />
        {!loading && !error && !adding && (
          <button className="wh-receive-btn" type="button" onClick={() => setAdding(true)}>+ Add contract</button>
        )}
      </div>

      <div className="wh-body">
        <div className="wh-list">
          {loading && <div className="wh-empty-pane">Loading…</div>}
          {!loading && error && <div className="wh-empty-pane wh-error">{error}</div>}
          {!loading && !error && (
            <table className="wh-table">
              <thead>
                <tr>
                  <th className="wh-th" style={{ cursor: 'default' }}>Contract ID</th>
                  <th className="wh-th" style={{ cursor: 'default' }}>Site</th>
                  <th className="wh-th" style={{ cursor: 'default' }}>Customer PO</th>
                  <th className="wh-th" style={{ cursor: 'default' }}>Auto-disposition</th>
                  <th className="wh-th" style={{ cursor: 'default' }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {contracts.length === 0 && !adding && (
                  <tr><td className="wh-td wh-empty" colSpan={COL_COUNT}>No contracts yet.</td></tr>
                )}
                {contracts.map((c) => (
                  <ContractRow
                    key={c.id || c.contract_id}
                    contract={c}
                    label={siteLabelById[c.site_id] || c.site_id || '—'}
                    callerEmail={callerEmail}
                    onSaved={loadContracts}
                  />
                ))}
                {adding && (
                  <AddContractRow
                    sites={sites}
                    callerEmail={callerEmail}
                    onSaved={loadContracts}
                    onCancel={() => setAdding(false)}
                  />
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
